import { Button, type ButtonProps, Circle, styled, type TextProps, View, XStack, type XStackProps, YStack, type YStackProps } from "tamagui";
import { backgroundPrimaryHex, backgroundTertiaryHex, strokeSecondaryHex, textPrimaryHex } from "../themes/colors-granted";
import { hairline } from "../utils";
export const StyledCircle = styled(Circle, {
  size: "$5",
  backgroundColor: "$groupedSecondary",
  zIndex: 2,
  justifyContent: "center",
  alignItems: "center",
  mb: "$3.5",
  variants: {
    whiteShadow: {
      true: {
        backgroundColor: "$background",
        shadowColor: "black",
        shadowOffset: {
          height: 4,
          width: 0
        },
        shadowRadius: 4,
        shadowOpacity: 0.08
      },
      false: {}
    }
  } as const
});
export const SquareIconWrapper = styled(XStack, {
  height: "$3",
  width: "$3",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "$3",
  backgroundColor: "$backgroundTertiary"
});
export const AnimatedYStackAnimationProps: YStackProps = {
  exitStyle: {
    transform: [{
      translateY: 50
    }],
    scale: 0,
    opacity: 0
  },
  enterStyle: {
    transform: [{
      translateY: 50
    }],
    scale: 0.5,
    opacity: 0
  },
  animation: "200ms easeCubic"
};
export const AnimatedYStack = styled(YStack, {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  transform: [{
    translateY: 0
  }],
  justifyContent: "flex-end",
  alignItems: "center"
});
export const AnimatedStackIconWrapper = styled(YStack, {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  top: 0,
  justifyContent: "center",
  alignItems: "center",
  animation: "bouncyTight",
  enterStyle: {
    opacity: 0,
    scale: 0.5
  },
  exitStyle: {
    opacity: 0
  }
});
export const AnimatedXStackAnimationProps: XStackProps = {
  enterStyle: {
    opacity: 0,
    scale: 0
  },
  exitStyle: {
    opacity: 0,
    scale: 0
  },
  animation: "150ms easeOut"
};
export const AnimatedXStack = styled(XStack, {
  opacity: 1,
  scale: 1,
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  f: 1,
  jc: "center",
  ai: "center"
});
export const AnimatedTextProps: TextProps = {
  position: "absolute",
  top: 0,
  right: 0,
  left: 0,
  f: 1,
  textAlign: "center",
  animation: "200ms easeCubic",
  enterStyle: {
    opacity: 0
  },
  exitStyle: {
    opacity: 0
  }
};
export const RefreshButtonClickProps: XStackProps = {
  shadowOffset: {
    width: 0,
    height: 2
  },
  shadowOpacity: 0.08,
  shadowRadius: 4,
  shadowColor: "black",
  pressStyle: {
    scale: 0.95
  },
  animation: "bouncyTight"
};
export const StyledRefreshButton = styled(XStack, {
  cursor: "pointer",
  gap: "$2",
  px: "$3",
  py: "$2",
  borderWidth: hairline,
  borderColor: "$strokeSecondary",
  backgroundColor: "$background",
  borderRadius: "$10",
  justifyContent: "center",
  alignItems: "center",
  ...RefreshButtonClickProps
});
export const StyledLinkStatus = styled(XStack, {
  justifyContent: "space-between",
  alignItems: "center",
  gap: "$2",
  px: "$3",
  py: "$2",
  borderRadius: "$10",
  borderWidth: hairline,
  borderColor: "$color4",
  backgroundColor: "$color3"
});
export const StyledMultiActionButton = styled(Button, {
  name: "StyledMultiActionButton",
  unstyled: true,
  cursor: "pointer",
  borderRadius: "$10",
  height: "$6",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingRight: "$4",
  paddingLeft: "$3",
  backgroundColor: backgroundPrimaryHex,
  borderWidth: 1,
  borderColor: strokeSecondaryHex,
  animation: "bouncyTight",
  hoverStyle: {
    backgroundColor: "white",
    borderColor: "$cream10",
    shadowColor: backgroundPrimaryHex,
    shadowOpacity: 0.8,
    shadowRadius: 10
  },
  pressStyle: {
    backgroundColor: "white",
    borderColor: "$cream10",
    shadowColor: backgroundPrimaryHex,
    shadowOpacity: 0.8,
    shadowRadius: 14,
    scale: 0.98
  },
  variants: {
    selected: {
      true: {
        borderColor: textPrimaryHex,
        shadowOffset: {
          width: 0,
          height: 4
        },
        shadowColor: "black",
        shadowOpacity: 0.08,
        shadowRadius: 8
      }
    }
  } as const
});
export const StyledNextActionButtonProps: ButtonProps = {
  unstyled: true,
  cursor: "pointer",
  br: "$10",
  height: "$6",
  width: "$7",
  animation: "bouncyTight",
  justifyContent: "center",
  alignItems: "center",
  color: backgroundPrimaryHex,
  backgroundColor: textPrimaryHex,
  hoverStyle: {
    backgroundColor: "#0a2d70"
  },
  pressStyle: {
    backgroundColor: "#143980",
    scale: 0.98
  }
};
export const StyledSkipActionButtonProps: ButtonProps = {
  unstyled: true,
  cursor: "pointer",
  br: "$10",
  height: "$6",
  width: "$12",
  animation: "bouncyTight",
  backgroundColor: "$background",
  borderWidth: hairline,
  borderColor: "$strokeSecondary",
  justifyContent: "center",
  alignItems: "center",
  color: textPrimaryHex,
  hoverStyle: {
    backgroundColor: "$background"
  }
};
export const StyledNextActionButton = styled(Button, {
  name: "StyledNextActionButton",
  unstyled: true,
  cursor: "pointer",
  br: "$10",
  height: "$6",
  width: "$7",
  animation: "bouncyTight",
  justifyContent: "center",
  alignItems: "center",
  color: backgroundPrimaryHex,
  backgroundColor: textPrimaryHex,
  hoverStyle: {
    backgroundColor: "#0a2d70"
  },
  pressStyle: {
    backgroundColor: "#143980",
    scale: 0.98
  }
});
export const FeedbackIconWrapper = styled(View, {
  position: "absolute",
  left: 8,
  top: 8,
  width: 16,
  height: 16,
  zIndex: 1
});
export const FeedbackButton = styled(Button, {
  unstyled: true,
  size: "$2.5",
  cursor: "pointer",
  backgroundColor: backgroundPrimaryHex,
  position: "relative",
  overflow: "hidden",
  width: "$2.5",
  animation: "bouncyTight",
  hoverStyle: {
    backgroundColor: backgroundTertiaryHex
  },
  pressStyle: {
    backgroundColor: backgroundTertiaryHex,
    scale: 0.97
  },
  variants: {
    selected: {
      true: {
        backgroundColor: backgroundTertiaryHex
      },
      false: {
        backgroundColor: backgroundPrimaryHex
      }
    }
  }
} as const);

// Normally we'd use $background, but in Granted’s theme (see
// component-themes.ts) for buttons,
// this maps buttons to dark blue. To avoid unintended global changes, we use $white1.
export const StyledSelectButton = styled(Button, {
  margin: 0,
  borderWidth: 1,
  borderColor: "$strokePrimary",
  color: "$color13",
  backgroundColor: "$white1",
  borderRadius: "$10",
  fontSize: "$4",
  fontWeight: "500",
  height: 50,
  animateOnly: ["transform", "borderColor"],
  animation: "100ms easeOut",
  hoverStyle: {
    borderColor: "$textSecondary",
    backgroundColor: "$white1"
  },
  pressStyle: {
    margin: 0,
    borderWidth: 2,
    borderColor: "$borderColorPress",
    backgroundColor: "$white1",
    scale: 0.98
  },
  variants: {
    selected: {
      true: {
        margin: 0,
        borderWidth: 2,
        borderColor: "$borderColorFocus",
        backgroundColor: "$white1"
      }
    }
  } as const
});
export const StyledPlaceholderLine = styled(XStack, {
  height: 8,
  width: "100%",
  backgroundColor: "$gray5",
  borderRadius: 2
});